import { XMarkIcon } from '@heroicons/react/20/solid';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { LinkIcon, PaperClipIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

import { MDXEditorMethods } from '@mdxeditor/editor';
import {
  FileAttachmentInput,
  LinkAttachmentInput
} from 'src/shared/components';
import { toastErrorIfFilesOverLimit } from 'src/shared/utils/fileSizeHelpers';

import MarkdownEditor, {
  COMPOSER_EDITOR_CLASS,
  COMPOSER_EDITOR_CONTENT_CLASS
} from 'src/shared/components/MarkdownEditor';
import { addToast, cn } from 'src/shared/components/ui';
import { ButtonV2, Select, Typography } from 'src/shared/components/ui';

import { useAskQuestion } from './question-content/hooks/useAskQuestion';
import { useClarificationFiles } from './question-content/hooks/useClarificationFiles';
import { useReplyQuestion } from './question-content/hooks/useReplyQuestion';

interface ProductUpdatesInputProps {
  onClose?: () => void;
  suppliers: string[];
  productId: string;
  clarificationId?: string;
  isReplying?: boolean;
  updateId?: number;
  hideCloseButton?: boolean;
  onScrollToBottom?: () => void;
}

type Link = {
  url: string;
  caption: string;
};

const LAST_QUESTION_STORAGE_KEY = 'lastQuestionValue';

type FormValues = {
  question: string;
  links: Link[];
  files: any[];
};

const ProductUpdatesInput: React.FC<ProductUpdatesInputProps> = ({
  onClose,
  suppliers,
  productId,
  clarificationId,
  isReplying,
  updateId,
  hideCloseButton = false,
  onScrollToBottom
}) => {
  const lastQuestionStorageKey = `${LAST_QUESTION_STORAGE_KEY}-${updateId ? updateId : productId}`;
  const { register, handleSubmit, watch, reset, control } = useForm<FormValues>(
    {
      defaultValues: {
        question: (() => {
          const savedQuestion = sessionStorage.getItem(lastQuestionStorageKey);

          return savedQuestion || '';
        })(),
        links: [],
        files: []
      }
    }
  );
  const question = watch('question');
  const files = watch('files');
  const links = watch('links');
  const askQuestionMutation = useAskQuestion();
  const replyQuestionMutation = useReplyQuestion();
  const clarificationFilesMutation = useClarificationFiles();

  const [shouldReset, setShouldReset] = useState(false);

  useEffect(() => {
    if (shouldReset) {
      // Clean up file previews
      if (files.length > 0) {
        files.forEach((file) => {
          if (file?.preview) {
            URL.revokeObjectURL(file.preview);
          }
        });
      }

      // Reset the form
      reset({
        question: '',
        links: [],
        files: []
      });
      // MDXEditor only reads its `markdown` prop on mount
      editorRef.current?.setMarkdown('');

      setShouldReset(false);
    }
  }, [shouldReset, files, reset, question, updateId]);

  // before render unmounts set question to session storage
  useEffect(() => {
    return () => {
      // Only save to session storage if we're not in a success state
      if (question && !isSubmitSuccessRef.current) {
        sessionStorage.setItem(lastQuestionStorageKey, question);
      }
    };
  }, [question, lastQuestionStorageKey]);

  const [selected, setSelected] = useState(new Set(['all']));

  const handleSelectionChange = (val: any) => {
    if (val.size > 0) {
      setSelected(new Set(val));
    }
  };
  const suppliersList = useMemo(() => {
    const parsed = suppliers.map((cloak) => ({
      key: cloak,
      label: cloak
    }));

    return [{ key: 'all', label: 'All suppliers' }, ...parsed];
  }, [suppliers]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'links'
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const editorRef = useRef<MDXEditorMethods>(null);
  const isSubmitSuccessRef = useRef(false);

  const {
    fields: fileFields,
    append: appendFile,
    remove: removeFile
  } = useFieldArray({
    control,
    name: 'files'
  });

  const onResetInput = () => {
    setShouldReset(true);
  };

  const isInputDisabled = useMemo(() => {
    return !question && links.length === 0 && files.length === 0;
  }, [question, links, files]);

  const renderLinks = () => {
    return fields.map((field, index) => {
      const url = watch(`links.${index}.url`);
      const caption = watch(`links.${index}.caption`);

      return (
        <LinkAttachmentInput
          key={field.id}
          urlInputProps={register(`links.${index}.url`)}
          captionInputProps={register(`links.${index}.caption`)}
          urlValue={url}
          captionValue={caption}
          onRemove={() => remove(index)}
        />
      );
    });
  };

  const renderFiles = () => {
    return fileFields.map((field, index) => {
      const file = watch(`files.${index}.file`);
      const caption = watch(`files.${index}.caption`);
      const preview = watch(`files.${index}.preview`);

      return (
        <FileAttachmentInput
          key={field.id}
          fileInputProps={register(`files.${index}.file`)}
          captionInputProps={register(`files.${index}.caption`)}
          fileValue={file}
          captionValue={caption}
          preview={preview}
          onRemove={() => removeFile(index)}
        />
      );
    });
  };

  const onSaveFiles = (
    files: any[],
    response: any,
    isReply?: boolean,
    clarificationId?: string
  ) => {
    if (files.length !== 0) {
      const filesBody = new FormData();

      files.forEach((file, i) => {
        filesBody.append(`files[${i}]`, file?.file);
        filesBody.append(`captions[${i}]`, file?.caption);
      });

      clarificationFilesMutation.mutate(
        {
          body: filesBody,
          id: isReply ? clarificationId : response.id,
          productId,
          replyId: isReply ? response.id : undefined
        },
        {
          onSuccess: () => {
            isSubmitSuccessRef.current = true;
            onResetInput();
            addToast({
              color: 'success',
              title: 'Your question has been sent. We will be in touch shortly.'
            });
            if (onScrollToBottom) {
              onScrollToBottom();
            }
            if (onClose) {
              onClose();
            }
          }
        }
      );
    }
  };

  const onSubmit = (data: any) => {
    const withFiles = data.files.length > 0;

    if (toastErrorIfFilesOverLimit({ files: data.files, addToast })) {
      return;
    }

    if (clarificationId) {
      const body = {
        links: data.links.filter((link: Link) => link.url.trim() !== ''),
        reply: data.question
      };

      replyQuestionMutation.mutate(
        {
          body,
          productId,
          withFiles,
          clarificationId
        },
        {
          onSuccess: (response) => {
            sessionStorage.removeItem(lastQuestionStorageKey);
            if (data.files.length > 0) {
              onSaveFiles(data.files, response, true, clarificationId);
            } else {
              isSubmitSuccessRef.current = true;
              sessionStorage.removeItem(lastQuestionStorageKey);
              addToast({
                color: 'success',
                title: 'Your reply has been sent. We will be in touch shortly.'
              });
              onResetInput();
              if (onScrollToBottom) {
                onScrollToBottom();
              }
              if (onClose) {
                onClose();
              }
            }
          }
        }
      );
    } else {
      const body: any = {
        product: productId,
        links: data.links.filter((link: Link) => link.url.trim() !== '')
      };

      if (isReplying) {
        body.clarification_update = updateId;
        body.reply = data.question;
      } else {
        body.question = data.question;
        body.title = 'Question';
      }

      const selectedValue = Array.from(selected)[0];

      if (selectedValue !== 'all') {
        body.supplier_cloak = selectedValue;
      }

      askQuestionMutation.mutate(
        { body, productId, withFiles },
        {
          onSuccess: (response) => {
            sessionStorage.removeItem(lastQuestionStorageKey);
            if (data.files.length > 0) {
              if (isReplying) {
                onSaveFiles(
                  data.files,
                  response?.clarification_data?.replies?.[0],
                  true,
                  response?.id
                );
              } else {
                onSaveFiles(data.files, response);
              }
            } else {
              isSubmitSuccessRef.current = true;
              sessionStorage.removeItem(lastQuestionStorageKey);
              addToast({
                color: 'success',
                title:
                  'Your question has been sent. We will be in touch shortly.'
              });
              onResetInput();
              if (onScrollToBottom) {
                onScrollToBottom();
              }
              if (onClose) {
                onClose();
              }
            }
          }
        }
      );
    }
  };

  return (
    <div className="relative flex flex-col border rounded-xl border-gray-4 bg-white">
      <form
        id={`ask-question-form-${updateId}`}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          name="question"
          control={control}
          render={({ field }) => (
            <MarkdownEditor
              ref={editorRef}
              markdown={field.value}
              onChange={field.onChange}
              placeholder={isReplying ? 'Add your reply' : 'Add your message'}
              className={cn(
                COMPOSER_EDITOR_CLASS,
                !hideCloseButton && '[&_[role=toolbar]]:pr-10'
              )}
              contentEditableClassName={COMPOSER_EDITOR_CONTENT_CLASS}
            />
          )}
        />
      </form>

      <div className="flex flex-wrap gap-2 mx-2 pb-2 border-t border-gray-4 pt-2 mt-2">
        {renderLinks()}
        {renderFiles()}
      </div>

      <div className="p-2 pt-0 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-grow">
          <ButtonV2
            isDisabled={askQuestionMutation.isPending}
            size="md"
            isIconOnly
            color="tertiary"
            onPress={() => {
              append({ url: '', caption: '' });
            }}
          >
            <LinkIcon className="size-4" />
          </ButtonV2>
          <input
            type="file"
            ref={fileInputRef}
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const files = e.target.files;

              if (files && files.length > 0) {
                const newEntries = Array.from(files).map((file) => ({
                  file,
                  caption: '',
                  preview: file.type.startsWith('image/')
                    ? URL.createObjectURL(file)
                    : null
                }));

                appendFile(newEntries);
                e.target.value = '';
              }
            }}
          />
          <ButtonV2
            isDisabled={askQuestionMutation.isPending}
            size="md"
            isIconOnly
            color="tertiary"
            onPress={() => fileInputRef.current?.click()}
          >
            <PaperClipIcon className="size-4" />
          </ButtonV2>
        </div>

        <div className="flex items-center justify-end gap-2 flex-1">
          {!isReplying && (
            <div className="flex items-center gap-2">
              <Typography
                variant="body-2"
                weight="medium"
                as="span"
                className="text-secondary-text"
              >
                About
              </Typography>
              <Select
                disallowEmptySelection
                defaultSelectedKeys={['all']}
                selectedKeys={selected}
                onSelectionChange={handleSelectionChange}
                label=""
                size="md"
                items={suppliersList}
              />
            </div>
          )}
          <ButtonV2
            form={`ask-question-form-${updateId}`}
            type="submit"
            size="md"
            isIconOnly
            color="primary"
            isDisabled={
              askQuestionMutation.isPending ||
              clarificationFilesMutation.isPending ||
              replyQuestionMutation.isPending ||
              isInputDisabled
            }
            isLoading={
              askQuestionMutation.isPending ||
              clarificationFilesMutation.isPending ||
              replyQuestionMutation.isPending
            }
          >
            <PaperAirplaneIcon className="size-4" />
          </ButtonV2>
        </div>
      </div>

      {!hideCloseButton && (
        <div className="absolute top-2 right-2">
          <ButtonV2
            radius="full"
            size="sm"
            onPress={() => onClose?.()}
            isIconOnly
            color="tertiary"
            isDisabled={askQuestionMutation.isPending}
          >
            <XMarkIcon className="size-3" />
          </ButtonV2>
        </div>
      )}
    </div>
  );
};

export default ProductUpdatesInput;
